import React from 'react';
import PropTypes from 'prop-types';
import cloneDeep from 'lodash/cloneDeep';
import { Container, Button } from '@sm/wds-react';
import { IconPlusCircle } from '@sm/wds-icons';
import { SINGLE_CHOICE } from '../../pages/NewSurvey/questionTypes';
import SurveyChoice from './SurveyChoice';
import { QuizChoice } from './QuizChoice';

const MultipleChoice = ({ question, surveyType, updateQuestion, maxAnsChoices, readOnlySurvey }) => {
  const handleAnswerChange = (idx, event) => {
    const currentQuestion = cloneDeep(question);
    const answers = [...currentQuestion.answers];
    answers[idx].answer = event.target.value;

    currentQuestion.answers = answers;
    updateQuestion(currentQuestion);
  };

  const moveAnswer = (idx, down) => {
    if (idx === 0 && !down) {
      return;
    }
    if (idx === question.answers.length - 1 && down) {
      return;
    }

    const currentQuestion = cloneDeep(question);
    const answers = [...currentQuestion.answers];

    const answer = answers[idx];
    const to = down ? idx + 1 : idx - 1;
    answers.splice(idx, 1);
    answers.splice(to, 0, answer);

    currentQuestion.answers = answers;
    updateQuestion(currentQuestion);
  };

  const deleteAnswer = idx => {
    const currentQuestion = cloneDeep(question);
    const answers = [...currentQuestion.answers];
    answers.splice(idx, 1);

    currentQuestion.answers = answers;
    updateQuestion(currentQuestion);
  };

  const addAnswer = () => {
    const currentQuestion = cloneDeep(question);
    const answers = [...currentQuestion.answers];
    answers.push({ answer: '', score: '' });

    currentQuestion.answers = answers;
    updateQuestion(currentQuestion);
  };

  const handleScoreChange = (idx, event) => {
    const val = event.target.value.replace(/[^0-9]+/g, '');
    const currentQuestion = cloneDeep(question);
    const answers = [...currentQuestion.answers];
    answers[idx].score = val;

    currentQuestion.answers = answers;
    updateQuestion(currentQuestion);
  };

  return (
    <Container data-testid="MultipleChoice">
      {question.answers.length > maxAnsChoices && (
        <div className="sm-new-survey__error">
          Multiple choice will only display a maximum of&nbsp;
          {maxAnsChoices}
          &nbsp;poll choices
        </div>
      )}
      {question.answers.map((answer, index) => {
        if (index < maxAnsChoices) {
          const id = `mc-${question.id}-answer-${index}`;
          return (
            <div key={id}>
              <If condition={surveyType !== 'quiz'}>
                <SurveyChoice
                  index={index}
                  readOnlySurvey={readOnlySurvey}
                  questionId={question.id}
                  answersLength={question.answers.length}
                  answerValue={answer.answer}
                  moveAnswer={moveAnswer}
                  deleteAnswer={deleteAnswer}
                  handleAnswerChange={handleAnswerChange}
                />
              </If>
              <If condition={surveyType === 'quiz'}>
                <QuizChoice
                  index={index}
                  readOnlySurvey={readOnlySurvey}
                  questionId={question.id}
                  answersLength={question.answers.length}
                  answer={answer}
                  moveAnswer={moveAnswer}
                  deleteAnswer={deleteAnswer}
                  handleAnswerChange={handleAnswerChange}
                  handleScoreChange={handleScoreChange}
                />
              </If>
            </div>
          );
        }
        return null;
      })}
      {question.answers.length < maxAnsChoices && !readOnlySurvey && (
        <Button size="sm" variant="text" onClick={() => addAnswer()} className="sm-new-survey__add-anschoice">
          <IconPlusCircle />
          Choice
        </Button>
      )}
    </Container>
  );
};

MultipleChoice.propTypes = {
  question: PropTypes.shape({
    id: PropTypes.string,
    question: PropTypes.string,
    questionType: PropTypes.string,
    answers: PropTypes.arrayOf(
      PropTypes.shape({
        answer: PropTypes.string,
        score: PropTypes.string,
      })
    ),
    min: PropTypes.number,
    max: PropTypes.number,
    allowComments: PropTypes.bool,
    required: PropTypes.bool,
    isNew: PropTypes.bool,
    isUpdated: PropTypes.bool,
  }),
  surveyType: PropTypes.string,
  updateQuestion: PropTypes.func,
  maxAnsChoices: PropTypes.number,
  readOnlySurvey: PropTypes.bool,
};

MultipleChoice.defaultProps = {
  question: {
    id: '',
    question: '',
    questionType: SINGLE_CHOICE,
    answers: [
      { answer: '', score: '' },
      { answer: '', score: '' },
      { answer: '', score: '' },
    ],
    min: 0,
    max: 100,
    allowComments: false,
    required: false,
    isNew: true,
    isUpdated: false,
  },
  surveyType: 'poll',
  updateQuestion: () => {},
  maxAnsChoices: 50,
  readOnlySurvey: false,
};

export default MultipleChoice;
